import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { FilterUsageGuideModalComponent } from '../../simple/filter-usage-guide-modal/filter-usage-guide-modal.component';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Base } from 'src/app/core/models/base.model';
import { UtilsService } from './../../../../core/services/utils.service';

import {
  Filter,
  QueryResult,
  Sort,
} from 'src/app/core/services/base/database.service';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { DownloadService } from 'src/app/core/services/download.service';
import { TableStatePersistenceService } from 'src/app/core/services/table-state-persistence.service';
import { AccordeonHostDirective } from './directives/accordeon-host.directive';
import { EstimatedPaginatorIntl } from './estimated-paginator-intl';

export type SelectionType = 'none' | 'all' | 'none-some' | 'all-some';
interface Selection {
  type: SelectionType;
  selected: any[];
  unselected: any[];
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // Scoped MatPaginatorIntl so this table's paginator can show an "(estimated)"
  // hint independently of every other paginator in the app.
  providers: [{ provide: MatPaginatorIntl, useClass: EstimatedPaginatorIntl }],
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(AccordeonHostDirective, { static: false })
  accHost!: AccordeonHostDirective;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  selection: Selection = {
    type: 'none',
    selected: [],
    unselected: [],
  };
  @Input('settings') settings!: TableSettings;
  @Input() refresh?: Observable<boolean>;
  @Output() event = new EventEmitter<any>();

  editedFilter: number = 0;
  tempFilter!: Filter;
  settings$!: BehaviorSubject<TableSettings>;
  currentSettings!: TableSettings;

  showAdvancedFilters = false;

  searchval?: string;

  displayedColumns: string[] = [];
  dataSource?: SBDataSource<any>;
  subscriptions: Subscription[] = [];

  parentColumnSettings: { [id: string]: Column[] } = {};

  filtersFromColumns: Filter[] = [];

  // filteringColumnName?: string;
  filteringColumn?: Column | null;

  expandedElement: any;

  @ContentChild('cell') cell!: TemplateRef<any>;

  /**
   * @description Subject for search input, debounced by 300ms.
   *  This is used to avoid multiple requests to the server.
   *  The searchChange$ emits the value of the search input and emit this value with your config to the settings$ to request the new list of data.
   */
  private searchChange$ = new Subject<string>();

  private tspsData: {
    path: string;
    tableName: string;
  } = {
    path: '',
    tableName: '',
  };

  constructor(
    private sbs: DynamicService<Base>,
    private utils: UtilsService,
    private downloadService: DownloadService,
    private router: Router,
    private tsps: TableStatePersistenceService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    @Inject(MatPaginatorIntl) private paginatorIntl: EstimatedPaginatorIntl,
  ) {}

  openFilterGuide(): void {
    this.dialog.open(FilterUsageGuideModalComponent, {
      width: '820px',
      maxWidth: '92vw',
      height: '85vh',
      panelClass: 'filter-usage-guide-dialog',
    });
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  onAdvancedFiltersChange(filters: Filter[]): void {
    const newSettings: TableSettings = {
      ...this.currentSettings,
      pageIndex: 0,
      advancedFilters: filters,
    };
    this.settings$.next(newSettings);
  }

  getAdvancedFilterCount(): number {
    return this.currentSettings?.advancedFilters?.length ?? 0;
  }

  // True when the column has at least one user-visible filter with a real value applied.
  // Empty strings, null and invisible (programmatic) filters do NOT count as active.
  hasActiveFilter(col: Column): boolean {
    if (!col.filters || col.filters.length === 0) return false;
    return col.filters.some((f) => {
      if (!f.visible) return false;
      const v = f.value;
      if (v === null || v === undefined) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    });
  }

  trackByColumnId(_index: number, col: Column): string {
    return col.id;
  }

  objectKeys = Object.keys;
  isArray = Array.isArray;

  ngOnInit(): void {
    let baseSettings = { ...this.settings };
    this.tspsData = {
      path: this.router.url,
      tableName: this.settings.entity,
    };
    const persistedState = this.tsps.loadState(
      this.tspsData.path,
      this.tspsData.tableName,
    );

    if (persistedState) {
      if (persistedState.pageIndex != null) {
        baseSettings.pageIndex = persistedState.pageIndex;
      }
      if (persistedState.pageSize != null) {
        baseSettings.pageSize = persistedState.pageSize;
      }
      if (persistedState.columns != null) {
        const newColumns = baseSettings.columns.map(col => {
          const persisted = persistedState.columns?.find(c => c.id === col.id);
          if (persisted) {
            // Preserve invisible (programmatic) filters from original settings
            const invisibleFilters = col.filters.filter(f => !f.visible);
            // Add visible (user-defined) filters from persisted state
            const visibleFilters = persisted.filters?.filter(f => f.visible) || [];
            
            return {
              ...col,
              visible: persisted.visible,
              sort: persisted.sort || col.sort,
              filters: [...invisibleFilters, ...visibleFilters]
            };
          }
          return col;
        });
        baseSettings.columns = newColumns;
      }

      if (persistedState.textFilterConfig != null) {
        baseSettings.textFilterConfig = persistedState.textFilterConfig;
      }

      if (persistedState.autocompleteTextFilterConfig != null) {
        baseSettings.autocompleteTextFilterConfig =
          persistedState.autocompleteTextFilterConfig;
      }
    }

    this.searchval = baseSettings.textFilterConfig?.value;

    this.settings$ = new BehaviorSubject(baseSettings);
    this.sbs.load(baseSettings.entity, baseSettings.lambdaEndpoint);

    this.subscriptions.push(
      this.settings$.subscribe((s) => {
        s.columns = s.columns.map((c) => ({
          ...c,
          hasActiveFilter: this.hasActiveFilter(c),
        }));
        this.currentSettings = s;
        const estimated = !!s.estimatedCount;
        if (this.paginatorIntl.estimated !== estimated) {
          this.paginatorIntl.estimated = estimated;
          this.paginatorIntl.changes.next();
        }
        this.filtersFromColumns = getFiltersFromColumns(s.columns);
        this.tsps.saveTableStateBySettings(
          s,
          this.tspsData.path,
          this.tspsData.tableName,
        );
      }),
    );

    // Handle displayed columns
    this.subscriptions.push(
      this.settings$.subscribe((s) => {
        // console.log('Table -> Settings$ subscription -> displayed columns', s);
        this.displayedColumns = [
          'select',
          ...s.columns.filter((c) => c.visible).map((c) => c.id),
        ];
      })
    );

    // Handle nested columns
    this.subscriptions.push(
      this.settings$.subscribe((s) => {
        console.log('Table -> Settings$ subscription -> nested columns', s);
        const withParent = s.columns.filter((c) => !!c.parent);
        const colStructure: { [id: string]: Column[] } = {};
        withParent.forEach((c) => {
          const parent = c.parent!;
          if (!colStructure[parent]) {
            colStructure[parent] = [];
          }
          colStructure[parent].push(c);
        });

        // console.log(
        //   'Table -> Settings$ subscription -> nested columns -> colStructure',
        //   colStructure
        // );
        this.parentColumnSettings = colStructure;
      })
    );

    this.subscriptions.push(
      this.searchChange$
        .pipe(
          tap((e) => console.log('Search Change', e)),
          debounceTime(500),
          tap((searchValue) => {
            if (!this.currentSettings.textFilterConfig) {
              return;
            }

            const newSettings = { ...this.currentSettings, pageIndex: 0 };
            newSettings.textFilterConfig = {
              column: newSettings.textFilterConfig?.column || 'id',
              value: searchValue,
              options: newSettings.textFilterConfig?.options,
            };
            this.settings$.next(newSettings);
          })
        )
        .subscribe()
    );

    this.dataSource = new SBDataSource<Base>(this.sbs, this.settings$, (estimated) => {
      if (this.paginatorIntl.estimated !== estimated) {
        this.paginatorIntl.estimated = estimated;
        this.paginatorIntl.changes.next();
      }
    });

    // Force a change detection tick as soon as a query resolves. Without this, the
    // recycled mat-table rows and the paginator range label can stay stale until some
    // unrelated event (e.g. clicking outside an open filter menu) triggers Angular's
    // next detection cycle — making a freshly-applied filter look like it "did nothing"
    // until the user clicks away.
    this.subscriptions.push(
      this.dataSource.loading$.subscribe((loading) => {
        if (!loading) {
          this.cdr.detectChanges();
        }
      }),
    );

    if (this.refresh != null) {
      this.subscriptions.push(
        this.refresh.subscribe((e) => {
          if (e) {
            this.settings$.next(this.settings);
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings'] && !changes['settings'].firstChange && this.settings$) {
      this.settings$.next(changes['settings'].currentValue);
    }
  }

  ngAfterViewInit() {
    console.log('After view init');

    const pageSub = this.paginator.page
      .pipe(
        tap((c) => {
          const newSettings: TableSettings = {
            ...this.currentSettings,
            pageIndex: c.pageIndex,
            pageSize: c.pageSize,
          };
          this.settings$.next(newSettings);
        })
      )
      .subscribe();

    this.subscriptions.push(pageSub);
  }

  openFilter(col: Column) {
    this.filteringColumn = col;
  }

  toggleVisibility(event: any, colIdx: number) {
    const newColumns = [...this.currentSettings.columns];
    newColumns[colIdx] = { ...newColumns[colIdx], visible: !newColumns[colIdx].visible };
    this.settings$.next({ ...this.currentSettings, columns: newColumns });
  }

  toggleVisibilityById(event: any, colid: string) {
    const colIdx = this.currentSettings.columns.findIndex((c) => c.id == colid);
    if (colIdx === -1) return;
    const newColumns = [...this.currentSettings.columns];
    newColumns[colIdx] = { ...newColumns[colIdx], visible: !newColumns[colIdx].visible };
    this.settings$.next({ ...this.currentSettings, columns: newColumns });
  }

  accordeonButtonClick(event: any, element: any) {
    this.expandedElement = this.expandedElement === element ? null : element;
    event.stopPropagation();
    if (this.expandedElement) {
      // console.log(element);
      setTimeout(() => {
        // trigger change detection
        this.loadAccordeonComponent(element);
      }, 50);
    }
  }

  loadAccordeonComponent(data: any) {
    const viewContainerRef = this.accHost.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent<any>(
      this.currentSettings.accordeonComponent
    );
    componentRef.instance.data = data;
  }

  /**
   * Call a custom handler defined in column.config.handler.
   * If handler is a function, call it with (element, column, this).
   * If handler is a string, emit an event with { type: handler, element, column }.
   */
  handleLinkHandler(col: Column, element: any) {
    const handler = col.config && col.config.handler;
    if (!handler) return;

    try {
      if (typeof handler === 'function') {
        // call with element, column and this component context
        (handler as Function)(element, col, this);
        return;
      }

      if (typeof handler === 'string') {
        // emit a named event that a parent can catch
        this.event.next({ type: handler, element, column: col });
        return;
      }

      // If handler is an object with run method (Action-like)
      if ((handler as any)?.run && typeof (handler as any).run === 'function') {
        (handler as any).run({ element, column: col, table: this });
        return;
      }
    } catch (err) {
      console.error('Error executing link handler', err);
    }
  }

  runAction(act: Action) {
    act.run(this);
  }

  /** Selection context */
  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    const atualType = this.selection.type;
    console.log('Toggle all rows with status:', atualType);
    if (atualType !== 'all') {
      this.selection = {
        type: 'all',
        selected: [],
        unselected: [],
      };
    } else {
      this.selection = {
        type: 'none',
        selected: [],
        unselected: [],
      };
    }

    // console.log('Selection after changes:', this.selection);
  }
  toggleOneRow(row: any) {
    const atualType = this.selection.type;
    switch (atualType) {
      case 'all': {
        const idx = this.selection.unselected.findIndex(
          (s) => Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
        );
        this.selection = {
          type: 'all-some',
          selected: [],
          unselected:
            idx !== -1
              ? this.selection.unselected.filter(
                  (s) =>
                    Object.keys(this.utils.getDeepDifferences(s, row)).length >
                  0
                )
              : [...this.selection.unselected, row],
        };
        break;
      }
      case 'all-some': {
        const idx = this.selection.unselected.findIndex(
          (s) => Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
        );

        this.selection = {
          type: 'all-some',
          selected: [],
          unselected:
            idx !== -1
              ? this.selection.unselected.filter(
                  (s) =>
                    Object.keys(this.utils.getDeepDifferences(s, row)).length >
                  0
                )
              : [...this.selection.unselected, row],
        };
        break;
      }
      case 'none': {
        const idx = this.selection.selected.findIndex(
          (s) => Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
        );
        this.selection = {
          type: 'none-some',
          selected:
            idx !== -1
              ? this.selection.selected.filter(
                  (s) =>
                    Object.keys(this.utils.getDeepDifferences(s, row)).length >
                  0
                )
              : [...this.selection.selected, row],
          unselected: [],
        };
        break;
      }
      case 'none-some': {
        const idx = this.selection.selected.findIndex(
          (s) => Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
        );
        this.selection = {
          type: 'none-some',
          selected:
            idx !== -1
              ? this.selection.selected.filter(
                  (s) =>
                    Object.keys(this.utils.getDeepDifferences(s, row)).length >
                  0
                )
              : [...this.selection.selected, row],
          unselected: [],
        };
        break;
      }
      default: {
        this.selection = {
          type: 'none',
          selected: [],
          unselected: [],
        };
      }
    }

    if (
      this.selection.selected.length === 0 &&
      this.selection.unselected.length === 0
    ) {
      this.selection = {
        type:
          this.selection.type === 'all-some'
            ? 'all'
            : this.selection.type === 'none-some'
              ? 'none'
              : 'none',
        selected: [],
        unselected: [],
      };
    }
    // console.log('Selection after changes:', this.selection);
  }

  /** The label for the checkbox on the passed row */
  isSelected(row: any): boolean {
    switch (this.selection.type) {
      case 'all':
        return true;
      case 'none':
        return false;
      case 'all-some':
        return (
          this.selection.unselected.findIndex(
            (s) =>
              Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
          ) === -1
        );
      case 'none-some':
        return (
          this.selection.selected.findIndex(
            (s) =>
              Object.keys(this.utils.getDeepDifferences(s, row)).length === 0
          ) !== -1
        );
      default:
        return false;
    }
  }

  /**
   * Sanitize a value for display in a table cell.
   * - If value contains data URI images (data:...), replace them with "[image]".
   * - Strip HTML tags (Quill content may include HTML) to avoid rendering raw HTML.
   * - Truncate long strings to a sensible length (default 200 chars).
   */
  formatCellValue(value: any, truncateAt = 200): string {
    if (value == null) return '';

    // If it's an object (e.g. JSON column), try to stringify
    let s: string;
    if (typeof value === 'object') {
      try {
        s = JSON.stringify(value);
      } catch (e) {
        s = String(value);
      }
    } else {
      s = String(value);
    }

    // Replace data URIs (images) with placeholder
    // e.g. data:image/png;base64,<...>
    s = s.replace(/data:\s*image\/[a-zA-Z0-9+.-]+;base64,[A-Za-z0-9+/=]+/g, '[image]');

    // Some Quill content may include HTML tags — strip them for safe display in table cell
    s = s.replace(/<[^>]*>/g, '');

    // Normalize whitespace
    s = s.replace(/\s+/g, ' ').trim();

    if (s.length > truncateAt) {
      return s.substring(0, truncateAt - 3) + '...';
    }
    return s;
  }
  checkboxLabel(row?: any): string {
    return `${this.isSelected(row) ? 'deselect' : 'select'} row ${
      row ? row.id : ''
    }`;
  }

  getSelectionLength(): number {
    if (!this.dataSource?.amount) return 0;

    switch (this.selection.type) {
      case 'all':
        return this.dataSource.amount;
      case 'none':
        return 0;
      case 'all-some':
        return this.dataSource.amount - this.selection.unselected.length;
      case 'none-some':
        return this.selection.selected.length;
      default:
        // Caso você precise de um valor padrão caso o tipo não corresponda a nenhum dos casos
        return 0;
    }
  }
  /** Selection context */

  columnChange(column: Column) {
    const nci = this.currentSettings.columns.findIndex((c) => c.id == column.id);
    if (nci === -1) return;
    const newColumns = [...this.currentSettings.columns];
    newColumns[nci] = column;
    const newSettings = { ...this.currentSettings, pageIndex: 0, columns: newColumns };
    this.settings$.next(newSettings);
    this.cdr.detectChanges();
  }

  emit(emitter: any, context: any) {
    this.event.next({ emitter, context });
  }

  handleSearchChange(ev: any) {
    this.searchChange$.next(ev.target.value);
  }

  download(attachment: { bytes: string; fileName: string }) {
    this.downloadService.download(attachment);
  }

  emitKycInfo(col: Column, kyc: any, element: any) {
    const emitter = col.config?.emitter ?? 'kyc-info';
    this.event.next({ type: emitter, kyc, element, column: col });
  }

  getKycList(value: any): any[] {
    if (!value) return [];
    if (this.isArray(value)) return value;
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return []; }
    }
    return [value];
  }

  /**
   * Handle click on table row. If anchorChild is configured on settings, navigate to
   * /firm/{entityName or settings.entity}/{prePath + id} or use element[anchorChild.id] as id.
   * If emitEvent is configured, emit an event instead of navigating.
   * Ignore clicks that originated from interactive elements (buttons, anchors, inputs, mat-menu, mat-checkbox etc.)
   */
  rowClick(ev: MouseEvent, element: any) {
    try {
      console.log('rowClick invoked', { element, target: ev.target });
      // If no anchorChild configured, nothing to do
      if (!this.currentSettings?.anchorChild) return;

      // If click originated from an interactive element, ignore
      const target = ev.target as HTMLElement;
      if (this.isInteractiveElement(target)) return;

      const ac = this.currentSettings.anchorChild;
      const idValue = ac.id && element ? element[ac.id] : undefined;
      const idToUse = idValue || (element && element.id) || undefined;
      if (!idToUse) return;

      // If emitEvent is configured, emit event instead of navigating
      if (ac.emitEvent) {
        this.event.next({ emitter: ac.emitEvent, context: { element, id: idToUse } });
        return;
      }

      // split prePath into segments (avoid creating a single segment with slashes)
      const preSegments = (ac.prePath || '')
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      // derive prefix from current URL: keep first non-empty segment (e.g. 'firm' or 'client')
      const currentUrl = this.router.url || window.location.pathname;
      const segments = (currentUrl || '').split('/').filter((s) => s && s.length > 0);
      const prefix = segments.length > 0 ? segments[0] : '';

      // Do NOT include baseEntity to avoid duplication (e.g. case/case/id). Build route as
      // [prefix?, ...preSegments, id]
      const routeArray = prefix
        ? [prefix, ...preSegments, idToUse]
        : [...preSegments, idToUse];

      console.log('Navigating to', routeArray, { newtab: !!ac.newtab });

      if (ac.newtab) {
        // open in a new tab using serialized URL
        const url = this.router.serializeUrl(this.router.createUrlTree(routeArray));
        window.open(url, '_blank');
      } else {
        this.router.navigate(routeArray).then((ok) => {
          console.log('router.navigate resolved', ok);
        });
      }
    } catch (err) {
      console.error('rowClick navigation error', err);
    }
  }

  private isInteractiveElement(el: HTMLElement | null): boolean {
    if (!el) return false;

    // climb up to check if inside an anchor, button, input, mat-menu, or element with role='button'
    let node: HTMLElement | null = el;
    for (let i = 0; i < 6 && node; i++) {
      const tag = node.tagName?.toLowerCase();
      if (
        tag === 'a' ||
        tag === 'button' ||
        tag === 'input' ||
        tag === 'select' ||
        tag === 'textarea' ||
        node.getAttribute('role') === 'button' ||
        node.classList.contains('mat-menu-item') ||
        node.classList.contains('mat-checkbox') ||
        node.classList.contains('mat-icon-button')
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  onAutocompleteSearchChange(search: string | null) {
    const autocompleteTextFilterConfig =
      this.currentSettings.autocompleteTextFilterConfig;
    if (!autocompleteTextFilterConfig) return;
    this.settings$.next({
      ...this.currentSettings,
      pageIndex: 0,
      autocompleteTextFilterConfig: {
        ...autocompleteTextFilterConfig,
        value: search || undefined,
      },
    });
  }
}

export interface Column {
  id: string;
  name: string;
  /** Computed by TableComponent — do not set externally. True when the column has a user-visible filter with a non-empty value. */
  hasActiveFilter?: boolean;
  type:
    | 'text'
    | 'link'
    | 'url'
    | 'events'
    | 'json'
    | 'date'
    | 'number'
    | 'boolean'
    | 'datetime'
    | 'options'
    | 'custom'
    | 'component'
    | 'accordeon'
    | 'multioptions'
    | 'downloadFile'
    | 'kyc-list';
  visible: boolean;
  order: number;
  component?: any;
  filters: Filter[];
  entityName?: string;
  entityPath?: string;
  parent?: string;
  sort?: 'asc' | 'desc';
  filterType?: 'text' | 'number' | 'date' | 'boolean' | 'options';
  config?: any;
  linkText?: string;

  // Used to access JSON objects. Root is the name of the column where the JSON is stored. Currently only supports one level.
  root?: string;

  /** Optional: allow this column to be used as anchor id when anchoring whole row */
  anchor?: boolean;
}

// Extend config typing for link handler
export interface LinkHandlerConfig {
  // Either a direct function (element, column, tableComponent) => void
  handler?:
    | ((element: any, column?: Column, table?: TableComponent) => void)
    | string;
  newtab?: boolean;
}

export class SBDataSource<T extends Base> extends MatTableDataSource<T> {
  private dataSubject = new BehaviorSubject<T[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private settings$: BehaviorSubject<TableSettings>;
  private settings!: TableSettings;
  public loading$ = this.loadingSubject.asObservable();
  public amount: number = 0;

  private subscriptions: Subscription[] = [];

  constructor(
    private sbs: DynamicService<Base>,
    settings$: BehaviorSubject<TableSettings>,
    private onEstimatedChange?: (estimated: boolean) => void,
  ) {
    super();
    this.settings$ = settings$;
  }

  override connect() {
    const settingss = this.settings$.pipe(
      tap((ev) => (this.settings = ev)),
      tap((ev) => this.sbs.load(ev.entity, ev.lambdaEndpoint)),
      // switchMap cancels the previous in-flight query() when a new settings$
      // emission arrives, so an earlier (broader) request resolving after a
      // newer (filtered) one can never overwrite dataSubject/amount with a
      // stale result (CPK-4465).
      switchMap(() => this.query())
    );

    this.subscriptions.push(
      settingss.subscribe(({ data, amount }) => {
        this.dataSubject.next(data);
        this.amount = amount;
      })
    );
    return this.dataSubject;
  }

  override disconnect(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  private query(): Observable<{ data: T[]; amount: number }> {
    this.loadingSubject.next(true);

    const sorts = getSortsFromColumns(this.settings.columns);

    const filters = [
      ...getFiltersFromColumns(this.settings.columns),
      ...(this.settings.advancedFilters ?? []),
    ];

    if (this.settings.autocompleteTextFilterConfig?.value) {
      const columnsToSearch =
        this.settings.autocompleteTextFilterConfig.columns;
      const search = this.settings.autocompleteTextFilterConfig.value;
      const parts =
        columnsToSearch?.map((column) => `${column}.ilike.%${search}%`) ?? [];
      const columnsWithRegex =
        this.settings.autocompleteTextFilterConfig.columnsWithRegex ?? [];
      for (const item of columnsWithRegex) {
        const normalized = (search || '').replace(
          new RegExp(item.regexToBeRemoved, 'g'),
          ''
        );
        if (normalized.length > 0) {
          parts.push(`${item.columnName}.ilike.%${normalized}%`);
        }
      }
      const ilikeFilter: Filter = {
        column: '',
        operator: 'or',
        visible: true,
        value: parts.join(','),
      };
      filters.push(ilikeFilter);
    }

    const search = this.settings.textFilterConfig?.value
      ? this.settings.textFilterConfig
      : undefined;

    // The fast separate-count path can't stay fast once a `completed` filter is
    // active — that filter forces Postgres to materialize the LATERAL joins it
    // otherwise elides (see CPK-4447 plan), so it falls back to the pre-existing
    // combined list+count behavior and reports the count as estimated.
    const hasCompletedFilter = filters.some((f) => f.column === 'completed');

    if (this.settings.separateExactCount && !hasCompletedFilter) {
      this.onEstimatedChange?.(false);

      return forkJoin([
        this.sbs
          .list(
            this.settings.pageIndex,
            this.settings.pageSize || 20,
            filters,
            sorts,
            search,
            false
          )
          .pipe(take(1)),
        this.sbs.count(filters, search, { limitOne: !!this.settings.headCountLimitOne }).pipe(take(1)),
      ]).pipe(
        catchError(() =>
          of([
            { data: [], count: 0 },
            { data: [], count: 0 },
          ] as [QueryResult<T>, QueryResult<T>])
        ),
        finalize(() => this.loadingSubject.next(false)),
        map(([listRes, countRes]) => ({
          data: (listRes as QueryResult<T>).data,
          amount: (countRes as QueryResult<T>).count ?? 0,
        }))
      );
    }

    if (this.settings.separateExactCount && hasCompletedFilter) {
      this.onEstimatedChange?.(true);
    }

    return this.sbs
      .list(
        this.settings.pageIndex,
        this.settings.pageSize || 20,
        filters,
        sorts,
        search,
        this.settings.countExact
      )
      .pipe(
        catchError(() => of({ data: [], count: 0 } as QueryResult<T>)),
        finalize(() => this.loadingSubject.next(false)),
        map((res) => ({
          data: (res as QueryResult<T>).data,
          amount: (res as QueryResult<T>).count ?? 0,
        }))
      );
  }
}

export interface TableSettings {
  columns: Column[];
  entity: string;
  search: string;

  /**
   * When set, list queries for this entity are routed through this lambda
   * endpoint (POST) instead of a direct Supabase REST call — for entities
   * whose grant can't be scoped by RLS (e.g. a materialized view). See
   * DatabaseService.lambdaListName.
   */
  lambdaEndpoint?: string;
  pageSize?: number;
  pageIndex?: number;
  accordeonComponent?: any;
  actionSettings?: ActionGroup[];

  /**
   * If provided, makes each non-interactive area of the row act as a link.
   * Example: { id: 'code', prePath: 'case/', entityName: 'case', newtab: false }
   */
  anchorChild?: AnchorChild;

  /**
   * @description If true, the table is enabled to filter the data by the textFilterConfig value and column.
   */
  textFilterConfig?: TextFilterConfig;
  autocompleteTextFilterConfig?: AutocompleteTextFilterConfig;

  /** When true, shows the "Filters" button that opens the advanced filter builder panel. */
  enableAdvancedFilters?: boolean;
  /** Currently active advanced filters (field/operator/value rows + date range). */
  advancedFilters?: Filter[];

  /**
   * When true, list queries request an exact row count (`count: 'exact'`)
   * instead of Postgres' estimated count — so pagination and the export dialog
   * show precise totals. Opt-in per table; defaults to estimated for performance.
   * NOTE: not viable on heavy views like cpv_funder_dashboard (exact count over a
   * LATERAL-aggregate filter statement-timeouts) — use `estimatedCount` there.
   */
  countExact?: boolean;

  /**
   * When true, the table's total is an estimate and the paginator shows
   * "… of N (estimated)". Opt-in; use on heavy views that can only afford an
   * estimated count so the approximate total is transparent to the user.
   */
  estimatedCount?: boolean;

  /**
   * When true, `SBDataSource.query()` fetches the page of rows and the total
   * count as two separate, parallel requests (`list(..., countExact: false)`
   * + `count(head: true)`) instead of one combined request with an embedded
   * count. This lets heavy views get a fast+exact total without paying the
   * cost of computing it alongside the paginated data (see CPK-4447).
   *
   * Only takes effect when no active filter targets the `completed` column —
   * that filter defeats the fast path on `cpv_cocounsel_clients_2` (forces
   * Postgres to materialize LATERAL joins it otherwise elides), so in that
   * case the table falls back to the pre-existing combined behavior and
   * reports the count as estimated via `onEstimatedChange`.
   *
   * Ignored (no behavior change, `onEstimatedChange` never called) unless
   * explicitly set — opt-in per table.
   *
   * Known gap (code review, CPK-4447): the fast path calls `DatabaseService.count()`,
   * which — unlike `list()` — does not resolve `id`/uuid filters using `contains`/
   * `startswith`/`endswith` through the `resolveUuidFilters` RPC. A table combining
   * `separateExactCount: true` with a text filter on an `id` column would get `count: 0`
   * from a swallowed Postgrest error instead of the real count. Not triggered by
   * Claimants today (no `id` text filter there) — resolve in `count()` before reusing
   * this flag on a table that has one.
   */
  separateExactCount?: boolean;
  /**
   * Only meaningful with `separateExactCount` (CPK-4911): the separate count HEAD goes
   * out with `limit=1` (see `DatabaseService.count()`), so PostgREST materializes one
   * row instead of computing every column of every row just to count them. The total
   * is unchanged. Opt-in per table — set only where measured.
   */
  headCountLimitOne?: boolean;
}

export interface AnchorChild {
  /** column id to use as identifier (e.g. 'code' or 'id') */
  id: string;
  /** optional prefix path */
  prePath?: string;
  /** override entity for routing */
  entityName?: string;
  /** open in new tab */
  newtab?: boolean;
  /** emit event instead of navigating (event type name) */
  emitEvent?: string;
}

export interface TextFilterConfig {
  column: string;
  value: string;
  options?: {
    config?: string | undefined;
    type?: 'plain' | 'phrase' | 'websearch' | undefined;
  };
}

export interface ColumnWithRegex {
  columnName: string;
  /** Regex pattern for characters to remove from search (e.g. '[^0-9]' for digits-only). */
  regexToBeRemoved: string;
}

export interface AutocompleteTextFilterConfig {
  columns: string[];
  value?: string;
  columnsWithRegex?: ColumnWithRegex[];
}

export interface Action {
  run: (context: any) => boolean | Promise<boolean>;
  id: string;
  displayName: string;
  /** optional icon name to show on plain buttons */
  icon?: string;
  /** optional css class to apply to the button */
  buttonClass?: string;
}

export interface ActionGroup {
  id: string;
  displayName: string;
  actions: Action[];
  /** render actions as plain buttons instead of a dropdown */
  plain?: boolean;
  /** optional css class for buttons in this group */
  buttonClass?: string;
  /** optional label text displayed to the left of the group's buttons */
  label?: string;
  /** when true, renders this group after the View Columns button */
  afterViewColumns?: boolean;
}

/**
 * Builds the Sort[] used for table queries (and exports). When no column has an
 * explicit `sort`, falls back to `id DESC` so the table and the export agree on
 * a deterministic ordering instead of relying on Postgres' implementation-defined
 * default for queries without ORDER BY.
 */
export function getSortsFromColumns(columns: Column[]): Sort[] {
  const explicit = columns
    .filter((c) => !!c.sort)
    .map((c) => ({ column: c.id, direction: c.sort! } as Sort));
  if (explicit.length > 0) return explicit;
  return [{ column: 'id', direction: 'desc' }];
}

export const JSON_NULL_FILTER_VALUE = '__NOT_STARTED__';

export function getFiltersFromColumns(columns: Column[]): Filter[] {
  return columns.reduce((prev: Filter[], current) => {
    let cf: Filter[] = current.filters;

    if (current.type === 'json' && current.root) {
      const jsonPath = `${current.root}->>"${String(current.id).replace(/"/g, '""')}"`;
      cf = cf.flatMap((f) => {
        if (f.value === null || f.value === undefined) return [];

        if (f.value === JSON_NULL_FILTER_VALUE) {
          return [{ ...f, column: jsonPath, operator: 'empty' as const }];
        }

        if (f.operator === 'eq') {
          return [{
            ...f,
            column: current.root!,
            operator: 'jsonbcontains' as const,
            value: { [current.id]: f.value },
          }];
        }
        if (f.operator === 'neq') {
          return [{
            ...f,
            column: current.root!,
            operator: 'jsonbnotcontains' as const,
            value: { [current.id]: f.value },
          }];
        }
        if (f.operator === 'in' && Array.isArray(f.value)) {
          const hasSentinel = f.value.includes(JSON_NULL_FILTER_VALUE);
          const realValues = f.value.filter((v) => v !== JSON_NULL_FILTER_VALUE);
          if (hasSentinel && realValues.length > 0) {
            return [{
              ...f,
              column: jsonPath,
              operator: 'or' as const,
              value: `${jsonPath}.is.null,${jsonPath}.in.(${realValues.join(',')})`,
            }];
          }
          if (hasSentinel) return [{ ...f, column: jsonPath, operator: 'empty' as const }];
          return [{ ...f, column: jsonPath, value: realValues }];
        }
        return [{ ...f, column: jsonPath }];
      });
    }

    return prev.concat(cf);
  }, []);
}
